// TEMP CORE-9199 remove after Oct 30
export const NOTE_TYPES = [
  'Hesitation',
  'Question',
  'Coaching',
  'Bug',
  'Admin action',
  'Other',
] as const

/** Option names copied verbatim from the Notion CUBK Live Sessions database. */
export const OUTCOMES = [
  'Scheduled',
  'Closed on call (self-serve)',
  'Closed on call - overturned by Udesh',
  'Handed off (named reason)',
  'Stalled (no handoff)',
  'Unfinished - continuing async',
  'No-show',
  'Rescheduled',
] as const

export const SELF_SERVE_CLOSE = 'Closed on call (self-serve)'

export const HANDOFF_REASONS = [
  'commingling',
  'unconnected_account_activity',
  'limited_plaid_history',
  'loan_detected',
  'personal_only_account',
  'connect_failed',
  'phantom_account',
  'too_much_user_work',
  'member_asked_for_help',
  'other',
] as const

export const ART_CAUSES = [
  'None',
  'Data problem',
  'Engine failure',
  'System failure',
  'Policy recreating engine logic',
  'Customer record unreliable',
] as const

export const CHECKBOX_FIELDS = [
  'Attended',
  'Recording on',
  'Coached out loud',
  'Admin action taken',
] as const

export type CheckboxField = (typeof CHECKBOX_FIELDS)[number]

export interface ShadowState {
  startedAt: string | null
  notes: string[]
  checks: Record<CheckboxField, boolean>
  outcome: string
  handoffReasons: string[]
  artCause: string
}

export const emptyState = (): ShadowState => ({
  startedAt: null,
  notes: [],
  checks: {
    Attended: false,
    'Recording on': false,
    'Coached out loud': false,
    'Admin action taken': false,
  },
  outcome: '',
  handoffReasons: [],
  artCause: '',
})

const storageKey = (userId: number) => `temp-shadow-session:${userId}`

const isShadowState = (value: unknown): value is ShadowState =>
  typeof value === 'object' &&
  value !== null &&
  'notes' in value &&
  Array.isArray(value.notes) &&
  'checks' in value &&
  typeof value.checks === 'object' &&
  value.checks !== null &&
  'handoffReasons' in value &&
  Array.isArray(value.handoffReasons)

export const loadState = (userId: number): ShadowState => {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return emptyState()
    const parsed: unknown = JSON.parse(raw)
    return isShadowState(parsed) ? parsed : emptyState()
  } catch {
    return emptyState()
  }
}

export const saveState = (userId: number, state: ShadowState): void => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(state))
  } catch {
    // Storage blocked: notes stay in page state only.
  }
}

export const toMarkdown = (state: ShadowState): string =>
  [
    '## Shadow notes',
    '',
    ...state.notes.map((note) => `- ${note}`),
    '',
    '## Scoring',
    '',
    ...CHECKBOX_FIELDS.map(
      (field) => `- ${field}: ${state.checks[field] ? 'Yes' : 'No'}`
    ),
    `- Outcome: ${state.outcome || '(not set)'}`,
    `- Handoff reason: ${state.handoffReasons.join(', ') || '(none)'}`,
    `- Art cause: ${state.artCause || '(not set)'}`,
  ].join('\n')
